/* eslint-disable no-nested-ternary */
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/styles';
import { isArray } from 'lodash';

import ProcessHandler from '@src/components/ProcessHandler';
import {
  DURATION,
  PACK_DURATION,
  PACKAGE_EXPIRED,
  PACKAGE_LEVEL_V3,
  PACKAGE_TYPE,
} from '@src/constants/package';
import api from '@src/apis';
import {
  filterPackagesV3,
  checkHasDiscountPackage,
  getPackageCurrentPrice,
  getTotalPricePackage,
  calculateSaleOffMonthlyPrice,
  roundSaleOffPercentage,
  checkFreePackage,
} from '@src/services/package';
import route from '@src/constants/route';
import { EVENT_TYPE } from '@src/constants';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import moment from 'moment';
import {
  checkVoucherByProduct,
  checkVoucherPriceOrPercent,
  getOptimalVouchersForPackage,
  verifyTime,
} from '@src/services/voucher';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import { checkVietNam } from '@src/utils/checkCountry';
import useIpLocation from '@src/hooks/useLocation';
import CreditUsageGuide from '@src/containers/CreditIntro/NewCreditIntroContent/CreditUsageGuide';
import { StyledPackages } from './index.style';
import PackageItem from './PackageItem';
import SwiperVoucherBanner from './SwiperVoucherBanner';
import CustomPackage from './CustomPackage';
import FreePackageRow from './FreePackageRow';
import FAQ from './FAQ';
import HeaderPricing from './HeaderPricing';
import FindYourPlanButton from './FindYourPlanButton';
import SubscriptionSuggestion from './SubscriptionSuggestion';
import TabDuration from './TabDuration';
import NewCustomPackage from './NewCustomPackage';
import NewPackageItem from './NewPackageItem';
import NewFreePackageRow from './NewFreePackageRow';
import PackagesComparison from './PackagesComparison';

const NUMBER_ELEMENTS_SHOW_CUSTOM_PACK_ROW = 4;

const NewPricingModelV3 = () => {
  const theme = useTheme();
  const isVietNam = checkVietNam();
  const location = useLocation();
  const history = useHistory();
  const { ip } = useIpLocation();
  const queryParams = new URLSearchParams(location.search);
  const [numberOfElements, setNumberOfElements] = useState(0);
  const { openSidebar } = useSelector((state) => state.layout);
  const [openSubscriptionSuggestion, setOpenSubscriptionSuggestion] =
    useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleOpenSubscriptionSuggestion = () => {
    if (isMobile) {
      history.push(route.MOBILE_SUBSCRIPTION_SUGGESTION);
      return;
    }
    setOpenSubscriptionSuggestion(true);
  };

  const handleCloseSubscriptionSuggestion = () => {
    setOpenSubscriptionSuggestion(false);
  };

  const getPackageDuration = () => {
    const packageDurationParam = queryParams.get('duration') || DURATION.YEARLY;
    return Object.values(DURATION).includes(packageDurationParam)
      ? packageDurationParam
      : DURATION.YEARLY;
  };

  const initialPackageDuration = getPackageDuration();

  const packageType = PACKAGE_TYPE.STUDIO;

  const [packageDuration, setPackageDuration] = useState(
    initialPackageDuration,
  );

  const [packages, setPackages] = useState([]);
  const [packagesDraft, setPackagesDraft] = useState([]);
  const [totalVoucher, setTotalVoucher] = useState([]);
  const [isVouchersLoaded, setIsVouchersLoaded] = useState(false);
  const [currentPackages, setCurrentPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasDiscountPackage, setHasDiscountPackage] = useState(false);
  const [firstPaidVoucher, setFirstPaidVoucher] = useState(null);
  const [voucherBannerToShow, setVoucherBannerToShow] = useState([]);
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const packagesRef = useRef();
  const packagesComparisonRef = useRef();

  const { usingPackage } = useSelector((state) => state.user);
  const { bannerVoucher } = useSelector((state) => state.voucher);
  const { user } = useSelector((state) => state.auth);
  const { getFeatureValue } = useFeatureFlags();
  const promotedMoePackageCode = getFeatureValue(
    FEATURE_KEYS.PROMOTED_PACKAGE_MOE,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  const newPricingOverviewEnabled = getFeatureValue(
    FEATURE_KEYS.NEW_PRICING_OVERVIEW,
    { ip, userId: user.id, email: user.email, phoneNumber: user.phoneNumber },
  );

  const packagesComparisonEnabled = getFeatureValue(
    FEATURE_KEYS.PACKAGES_COMPARISON,
    { ip, userId: user.id, email: user.email, phoneNumber: user.phoneNumber },
  );

  const creditsUsageGuideEnabled = getFeatureValue(
    FEATURE_KEYS.CREDITS_USAGE_GUIDE,
    { ip, userId: user.id, email: user.email, phoneNumber: user.phoneNumber },
  );

  const buyContainerMinHeight = useMemo(() => {
    const isFreePackageV3 = checkFreePackage(user?.packageCode);
    const isExpired =
      user?.packageExpiryDate && new Date(user.packageExpiryDate) < new Date();
    const hasAnyBuyTopup =
      !!usingPackage?.topUpPackageCode && !isFreePackageV3 && !isExpired;

    const usingPkgHasMostPopular = packages?.some(
      (p) => p.level === usingPackage?.level && p.mostPopular,
    );

    // Calculate max height across all cards:
    // - Card with mostPopular: button(48) + popularity(32) = 80
    // - Card with topup: button(48) + gap(12) + topup(48) = 108
    // - Card with both: button(48) + popularity(32) + gap(12) + topup(48) = 140
    const buttonHeight = 48;
    const gapHeight = 12;
    const topupHeight = 48;
    const popularityHeight = 32;
    const mostPopularHeight = buttonHeight + popularityHeight;
    const topupCardHeight = hasAnyBuyTopup
      ? usingPkgHasMostPopular
        ? buttonHeight + popularityHeight + gapHeight + topupHeight
        : buttonHeight + gapHeight + topupHeight
      : 0;
    return Math.max(80, mostPopularHeight, topupCardHeight);
  }, [packages, user, usingPackage]);

  const handleSetCurrentPackages = (duration, allPackages) => {
    const displayPackages = filterPackagesV3({
      duration,
      allPackages,
      packageType,
    });

    // Sort packages free put to the end of list
    setCurrentPackages(
      displayPackages?.sort((a, b) =>
        a.rank === 1 ? 1 : b.rank === 1 ? -1 : a.rank - b.rank,
      ),
    );
    const isHasDiscountPackage = checkHasDiscountPackage(displayPackages);
    setHasDiscountPackage(isHasDiscountPackage);
  };

  const handleDiscountEnd = () => {
    const isHasDiscountPackage = checkHasDiscountPackage(currentPackages);
    setHasDiscountPackage(isHasDiscountPackage);
  };

  const handleChangePackageDuration = (value) => {
    setPackages(packagesDraft);
    setPackageDuration(value);
    queryParams.set('duration', value);
    history.replace({ search: queryParams.toString() });
  };

  // Lấy tất cả các gói cước
  const fetchPackages = async () => {
    setLoading(true);
    const pkgQuery = { type: packageType, active: true, purchasable: true };
    if (packageType === PACKAGE_TYPE.DUBBING) {
      pkgQuery.type = PACKAGE_TYPE.STUDIO;
    }
    const { status, result } = await api.packages.getPackages(pkgQuery);
    setLoading(false);
    if (status) {
      setPackages(result.packages);
      setPackagesDraft(result.packages);
    }
  };

  const fetchVoucherForFirstPaidUser = async () => {
    const response = await api.vouchers.getVoucherForFirstPaidOrder();
    if (response.status) {
      const voucher = response.result?.[0];
      const satisfiedTime = verifyTime(voucher);
      if (satisfiedTime) setFirstPaidVoucher(voucher);
    }
  };

  const fetchTotalVouchers = async () => {
    try {
      const data = await api.vouchers.getVouchers();
      if (data.status) {
        setTotalVoucher(data.result);
      }
    } catch (error) {
      setTotalVoucher([]);
    } finally {
      setIsVouchersLoaded(true);
    }
  };

  // Lấy % giảm giá tối đa theo năm

  const getCorrespondMonthPkg = ({ packageName, level, expiresIn }) => {
    if (expiresIn === PACKAGE_EXPIRED.MONTHLY) {
      return packages.find(
        (pkg) =>
          pkg.name[language] === packageName &&
          pkg.level === level &&
          pkg?.expiresIn === PACK_DURATION.MONTH,
      );
    }

    return packages.find(
      (pkg) =>
        pkg.name[language] === packageName &&
        pkg.level === level &&
        pkg?.expiresIn !== expiresIn &&
        pkg?.expiresIn === PACK_DURATION.MONTH,
    );
  };

  // Get best voucher type price or percent and package price
  const getBestVoucherAndPackagePrice = (pkg) => {
    const { startAt, endAt } = pkg.discountConfig || {};
    const isDiscountPackage = startAt && moment().isBetween(startAt, endAt);
    const { price, usdPrice } = getPackageCurrentPrice(pkg, true);
    const originalPrice = isVietNam ? price : usdPrice;

    const vouchers = getOptimalVouchersForPackage({
      pkg,
      vouchers: totalVoucher,
    });
    const [bestVoucher] = vouchers || [];
    const isBestVoucherPriceOrPercent = checkVoucherPriceOrPercent(bestVoucher);
    const bestVoucherPriceOrPercent = isBestVoucherPriceOrPercent
      ? bestVoucher
      : null;

    return {
      totalPrice: getTotalPricePackage(
        pkg,
        isDiscountPackage,
        bestVoucherPriceOrPercent,
        isVietNam,
        true,
      ),
      bestVoucher: bestVoucherPriceOrPercent,
      isDiscountPackage,
      originalPrice,
    };
  };

  const saveUpToPercent = () => {
    const packagesYear = packages?.flatMap((pkg) => {
      if (
        packageType === PACKAGE_TYPE.API &&
        pkg.expiresIn === PACKAGE_EXPIRED.YEARLY
      )
        return pkg;
      return pkg.expiresIn === PACK_DURATION.YEAR ? pkg : [];
    });

    const percents = packagesYear?.map((pkg) => {
      const totalPrice = isVietNam ? pkg?.price : pkg.usdPrice;

      const correspondMonthPkg = getCorrespondMonthPkg({
        packageName: pkg?.name?.[language],
        level: pkg.level,
        expiresIn: pkg.expiresIn,
      });
      const correspondMonthPrice = isVietNam
        ? correspondMonthPkg?.price
        : correspondMonthPkg?.usdPrice;

      if (!correspondMonthPrice || !totalPrice) return 0;

      const monthByDuration = Math.round(pkg?.expiresIn / 30);
      const saleOffPercentage =
        (1 - totalPrice / (correspondMonthPrice * monthByDuration)) * 100;
      return saleOffPercentage;
    });

    return roundSaleOffPercentage(
      percents.length > 0 ? Math.max(...percents) : 0,
    );
  };

  const checkShowBannerVoucherNew = () => {
    if (!bannerVoucher || !bannerVoucher.length) return false;
    const result = bannerVoucher.filter((voucher) => {
      const { endTime } = voucher;
      const isValidTime = endTime && moment().isBefore(endTime);
      if (!isValidTime) return false;

      const { applyAllPackages, packages: validPackages } =
        voucher?.policy || {};
      if (applyAllPackages) return true;

      return validPackages.some(
        (pkg) => pkg.type.toLowerCase() === packageType.toLowerCase(),
      );
    });

    return result;
  };

  const checkExpiredDate = (expiryDate) => {
    if (!expiryDate) return false;
    return moment().isAfter(expiryDate);
  };

  const getUsingPackage = (pkgType) => {
    switch (pkgType) {
      case PACKAGE_TYPE.STUDIO: {
        const studioPackage = packages.find(
          (pkg) => pkg.code === user?.packageCode,
        );
        return {
          ...studioPackage,
          isExpires: checkExpiredDate(user?.packageExpiryDate),
        };
      }
      case PACKAGE_TYPE.API: {
        const apiPackage = packages.find(
          (pkg) => pkg.code === user?.apiPackage?.packageCode,
        );
        return {
          ...apiPackage,
          isExpires: checkExpiredDate(user?.apiPackage?.packageExpiryDate),
        };
      }
      default:
        return null;
    }
  };

  const getPrice = (pkg) => {
    // Calculate sale off percentage
    const { totalPrice, bestVoucher, isDiscountPackage, originalPrice } =
      getBestVoucherAndPackagePrice(pkg);

    if (!originalPrice || !totalPrice) return 0;

    const saleOffPercentage = (1 - totalPrice / originalPrice) * 100;
    const saleOffPercentageRounded = roundSaleOffPercentage(saleOffPercentage);

    // Calculate total price and monthly price with sale off
    const monthlyPriceWithSaleOff = calculateSaleOffMonthlyPrice(
      pkg,
      isDiscountPackage,
      bestVoucher,
      isVietNam,
      packageDuration,
    );

    return {
      totalPrice,
      monthlyPriceWithSaleOff,
      saleOffPercentageRounded,
    };
  };
  const isShowRowPackageFree = useMemo(
    () => numberOfElements >= NUMBER_ELEMENTS_SHOW_CUSTOM_PACK_ROW,
    [numberOfElements],
  );

  const scrollToPackagesComparison = useCallback(() => {
    if (!packagesComparisonRef.current) return;
    packagesComparisonRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [packagesComparisonRef]);

  const renderNormalPackageItem = (pkg) => {
    const correspondMonthPkg = getCorrespondMonthPkg({
      packageName: pkg?.name?.[language],
      level: pkg.level,
      expiresIn: pkg.expiresIn,
    });
    const correspondMonthPrice = isVietNam
      ? correspondMonthPkg?.price
      : correspondMonthPkg?.usdPrice;

    const { totalPrice, monthlyPriceWithSaleOff, saleOffPercentageRounded } =
      getPrice(pkg);

    const packageTopUp =
      packages.find((item) => item.code === pkg.topUpPackageCode) || {};

    const isFreePackage = checkFreePackage(pkg?.code);

    if (
      isFreePackage &&
      isShowRowPackageFree &&
      packageDuration === DURATION.YEARLY
    ) {
      return newPricingOverviewEnabled ? (
        <NewFreePackageRow pkg={pkg} duration={packageDuration} />
      ) : (
        <FreePackageRow pkg={pkg} duration={packageDuration} />
      );
    }
    return (
      <>
        {newPricingOverviewEnabled ? (
          <NewPackageItem
            packagesComparisonEnabled={packagesComparisonEnabled}
            scrollToPackagesComparison={scrollToPackagesComparison}
            packages={packages}
            saleOffPercentage={saleOffPercentageRounded}
            monthlyPriceWithSaleOff={monthlyPriceWithSaleOff || 0}
            correspondMonthPrice={correspondMonthPrice}
            totalPrice={totalPrice}
            pkg={pkg}
            isMobile={isMobile}
            duration={packageDuration}
            hasDiscountPackage={hasDiscountPackage}
            onDiscountEnd={handleDiscountEnd}
            firstPaidVoucher={firstPaidVoucher}
            canUseVouchers={getOptimalVouchersForPackage({
              pkg,
              vouchers: totalVoucher,
            })}
            currentUsingPackage={getUsingPackage(pkg?.type)}
            packageTopUp={packageTopUp}
            setPackages={setPackages}
            correspondMonthPkg={correspondMonthPkg}
            buyContainerMinHeight={buyContainerMinHeight}
          />
        ) : (
          <PackageItem
            packages={packages}
            saleOffPercentage={saleOffPercentageRounded}
            monthlyPriceWithSaleOff={monthlyPriceWithSaleOff || 0}
            correspondMonthPrice={correspondMonthPrice}
            totalPrice={totalPrice}
            pkg={pkg}
            isMobile={isMobile}
            duration={packageDuration}
            hasDiscountPackage={hasDiscountPackage}
            onDiscountEnd={handleDiscountEnd}
            firstPaidVoucher={firstPaidVoucher}
            canUseVouchers={getOptimalVouchersForPackage({
              pkg,
              vouchers: totalVoucher,
            })}
            currentUsingPackage={getUsingPackage(pkg?.type)}
            packageTopUp={packageTopUp}
            setPackages={setPackages}
            correspondMonthPkg={correspondMonthPkg}
          />
        )}
      </>
    );
  };
  const renderCustomPackageItem = (pkg) =>
    newPricingOverviewEnabled ? (
      <NewCustomPackage
        packagesComparisonEnabled={packagesComparisonEnabled}
        scrollToPackagesComparison={scrollToPackagesComparison}
        pkg={pkg}
        isMobile={isMobile}
        duration={packageDuration}
        hasDiscountPackage={hasDiscountPackage}
        firstPaidVoucher={firstPaidVoucher}
        vouchers={totalVoucher}
        currentUsingPackage={getUsingPackage(pkg?.type)}
        buyContainerMinHeight={buyContainerMinHeight}
      />
    ) : (
      <CustomPackage
        packages={packages}
        pkg={pkg}
        isMobile={isMobile}
        duration={packageDuration}
        hasDiscountPackage={hasDiscountPackage}
        firstPaidVoucher={firstPaidVoucher}
        vouchers={totalVoucher}
        currentUsingPackage={getUsingPackage(pkg?.type)}
      />
    );

  const renderPackages = (pkgs = []) => {
    const normalPackage = (pkg) => renderNormalPackageItem(pkg);
    const customPackage = (pkg) => renderCustomPackageItem(pkg);

    return (
      <div className="packages">
        {pkgs?.map((pkg) => (
          <React.Fragment key={`${pkg?.code}`}>
            {pkg?.level.includes(PACKAGE_LEVEL_V3.CUSTOM)
              ? customPackage(pkg)
              : normalPackage(pkg)}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const filterVoucherBannerByProduct = () => {
    const result =
      isArray(bannerVoucher) &&
      bannerVoucher.length > 0 &&
      bannerVoucher.filter((voucher) => {
        const check = checkVoucherByProduct({ voucher, product: packageType });
        return check;
      });

    return result;
  };

  useEffect(() => {
    fetchVoucherForFirstPaidUser();
    fetchTotalVouchers();
    return () => {
      setFirstPaidVoucher(null);
      setTotalVoucher([]);
      setIsVouchersLoaded(false);
    };
  }, []);

  useEffect(() => {
    fetchPackages();
  }, [packageType]);

  useEffect(() => {
    const result = filterVoucherBannerByProduct();
    setVoucherBannerToShow(result);
  }, [bannerVoucher, packageType]);

  useEffect(() => {
    handleSetCurrentPackages(packageDuration, packages);
  }, [usingPackage, packageDuration, packages]);

  useEffect(() => {
    if (!promotedMoePackageCode || !isVouchersLoaded) return;

    const promotedPackage = packages.find(
      (pkg) => pkg.code === promotedMoePackageCode,
    );
    if (!promotedPackage) return;

    const vouchers = getOptimalVouchersForPackage({
      pkg: promotedPackage,
      vouchers: totalVoucher,
    });

    const [bestPromotedVoucher] = vouchers || [];

    const plan = promotedPackage?.code?.split('-')[1];

    window.dataLayer.push({
      event: EVENT_TYPE.VIEW_PRICING,
      userId: user?.id,
      email: user.email,
      product: packageType,
      plan,
      period: packageDuration,
      promoted_package_price: promotedMoePackageCode?.price,
      promoted_package_code: promotedMoePackageCode,
      promoted_package_voucher_code: bestPromotedVoucher?.code,
      promoted_package_voucher_type: bestPromotedVoucher?.type,
      promoted_package_voucher_amount: bestPromotedVoucher?.value,
    });
  }, [packages?.length, promotedMoePackageCode, isVouchersLoaded]);
  const updateNumberOfElements = () => {
    const PACKAGE_WIDTH = 318;
    const padding = 22;
    if (packagesRef.current) {
      const containerWidth = packagesRef?.current?.offsetWidth - padding;
      if (containerWidth > 0) {
        const number = Math.floor(containerWidth / PACKAGE_WIDTH);
        setNumberOfElements(number);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('resize', updateNumberOfElements);
    updateNumberOfElements();
    return () => {
      window.removeEventListener('resize', updateNumberOfElements);
    };
  }, [updateNumberOfElements, packagesRef?.current, openSidebar]);

  useEffect(() => {
    const syncHeight = () => {
      const elements = document.querySelectorAll('.pricing-top-section');
      if (!elements.length) return;

      // Reset height to auto to get natural height
      elements.forEach((element) => {
        const el = element;
        el.style.minHeight = 'auto';
      });

      // Find max height
      let maxHeight = 0;
      elements.forEach((el) => {
        const height = el.offsetHeight;
        if (height > maxHeight) maxHeight = height;
      });

      // Set max height to all elements
      elements.forEach((element) => {
        const el = element;
        el.style.minHeight = `${maxHeight}px`;
      });
    };

    // Run on resize
    window.addEventListener('resize', syncHeight);

    const timeoutId = setTimeout(syncHeight, 0);

    return () => {
      window.removeEventListener('resize', syncHeight);
      clearTimeout(timeoutId);
    };
  }, [currentPackages, numberOfElements, packageDuration]);

  return (
    <>
      {newPricingOverviewEnabled && (
        <HeaderPricing
          onOpenSubscriptionSuggestion={handleOpenSubscriptionSuggestion}
          onCloseSubscriptionSuggestion={handleCloseSubscriptionSuggestion}
          openSubscriptionSuggestion={openSubscriptionSuggestion}
          newPricingOverviewEnabled={newPricingOverviewEnabled}
        />
      )}
      <StyledPackages
        isVietNam={isVietNam}
        num={currentPackages.length}
        isMobile={isMobile}
        overflowX={newPricingOverviewEnabled ? 'clip' : 'hidden'}
      >
        <div
          className={
            !isMobile
              ? 'duration-header'
              : !newPricingOverviewEnabled && 'duration-header-mobile'
          }
        >
          {!newPricingOverviewEnabled && (
            <HeaderPricing
              onOpenSubscriptionSuggestion={handleOpenSubscriptionSuggestion}
              onCloseSubscriptionSuggestion={handleCloseSubscriptionSuggestion}
              openSubscriptionSuggestion={openSubscriptionSuggestion}
              newPricingOverviewEnabled={newPricingOverviewEnabled}
            />
          )}

          <TabDuration
            percentage={saveUpToPercent()}
            packageDuration={packageDuration}
            handleChangePackageDuration={handleChangePackageDuration}
          />
        </div>
        {!isMobile && (
          <FindYourPlanButton onClick={handleOpenSubscriptionSuggestion} />
        )}
        <div className="container-pricing">
          {/* Pricing banner */}
          {checkShowBannerVoucherNew() &&
            voucherBannerToShow.length > 0 &&
            !isMobile && (
              <div className="packages-wrapper">
                <SwiperVoucherBanner bannerVoucher={voucherBannerToShow} />
              </div>
            )}

          {/* List packages */}
          <div ref={packagesRef} className="packages-wrapper">
            <ProcessHandler
              loading={loading}
              mt="30px"
              mb="30px"
              align="center"
            >
              {renderPackages(currentPackages)}
            </ProcessHandler>
          </div>
        </div>

        <CommonDialog
          open={openSubscriptionSuggestion}
          width="1000px"
          styleContent={{
            padding: '24px',
            overflowX: 'hidden !important',
          }}
          disableAutoClose
          PaperProps={{
            sx: {
              height: '620px',
            },
          }}
          onClose={handleCloseSubscriptionSuggestion}
          contentComponent={
            <SubscriptionSuggestion
              packagesData={packages}
              allVouchers={totalVoucher}
            />
          }
          title={t('findYourPlan')}
        />
      </StyledPackages>
      <StyledPackages
        isVietNam={isVietNam}
        num={currentPackages.length}
        isMobile={isMobile}
      >
        {!loading && (
          <div className="packages-wrapper">
            {packagesComparisonEnabled && currentPackages?.length > 0 && (
              <PackagesComparison
                ref={packagesComparisonRef}
                saveUpToPercent={saveUpToPercent()}
                onChangeTab={handleChangePackageDuration}
                activeGranularity={packageDuration}
                isMobile={isMobile}
                canUseVouchers={(item) =>
                  getOptimalVouchersForPackage({
                    pkg: item,
                    vouchers: totalVoucher,
                  })
                }
                currentPackages={currentPackages}
                packages={packages}
                numberOfElements={numberOfElements}
                totalVoucher={totalVoucher}
                newPricingOverviewEnabled={newPricingOverviewEnabled}
              />
            )}
            {creditsUsageGuideEnabled && (
              <CreditUsageGuide
                isMobile={isMobile}
                showTitle
                numberOfElements={numberOfElements > 4 ? 4 : numberOfElements}
              />
            )}
            <FAQ
              numberOfElements={numberOfElements > 4 ? 4 : numberOfElements}
              packages={packages}
            />
          </div>
        )}
      </StyledPackages>
    </>
  );
};

export default NewPricingModelV3;
